import { trigger, transition, style, animate } from "@angular/animations";
import { Component, OnInit, ViewChild, Input, inject, DestroyRef } from "@angular/core";
import { FormGroup, FormControl, Validators, ValidatorFn, AbstractControl } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { MatPaginator } from "@angular/material/paginator";
import { MatSelectChange } from "@angular/material/select";
import { MatSort } from "@angular/material/sort";
import { MatTableDataSource } from "@angular/material/table";
import { environment } from '@env/environment';
import { ActivatedRoute, Router } from "@angular/router";
import { AuthService } from "@shared/auth/auth.service";
import { SharedService } from "@shared/services/shared.service";
import { AddressComponent } from "app/components/reusable/address/address.component";
import { IHospitalLicense } from "app/models/licenses/hospital/hospital-license";
import { IRepresentativeContact } from "app/models/licenses/hospital/representative-contact";
import { ISearch } from "app/models/licenses/search";
import { IStaff } from "app/models/user/staff";
import { HospitalLicenseService } from "app/services/license/hospital/hospital-license.service";
import { HospitalContactsExportComponent } from "../hospital-contacts-export/hospital-contacts-export.component";
import { startWith } from "rxjs";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { HospitalChiefNurseValidator } from "@shared/validators/hospital-chief-nurse-validator";
import { HospitalContactNameErrorStateMatcher, hospitalContactNameLabels, hospitalContactNamesValidator } from "@shared/validators/hospital-contact-names-validator";

@Component({
  selector: 'app-hospital',
  templateUrl: './hospital.component.html',
  styleUrls: ['./hospital.component.scss'],
  animations: [
    trigger('slideLeft', [
      transition(':enter', [
        style({ transform: 'translateX(-100%)' }),
        animate('200ms ease-in', style({ transform: 'translateX(0%)' }))
      ]),
      transition(':leave', [
        animate('200ms ease-in', style({ transform: 'translateX(-100%)' }))
      ])
    ]),
    trigger('slideRight', [
      transition(':enter', [
        style({ transform: 'translateX(100%)' }),
        animate('200ms ease-in', style({ transform: 'translateX(0%)' }))
      ]),
      transition(':leave', [
        animate('200ms ease-in', style({ transform: 'translateX(100%)' }))
      ])
    ])
  ]
})

export class HospitalComponent implements OnInit {
  @ViewChild(AddressComponent) addresses!: AddressComponent;
  @Input() assigned: boolean = false;

  public isProduction: boolean = environment.production;
  public id: number = 0;
  public hospital: string = "Hospital";
  public renewal: boolean = false;
  public editing: boolean = false;
  public paymentsLoaded: boolean = false;
  public hospitalDocumentTypes: string[] = ['Admin Signature', 'Certification Document', 'Other'];
  public search: ISearch = {
    searchTerm: '',
    entityGuid: false,
    entityName: false,
    assignedTo: '',
    includeArchived: false,
    statuses: []
  }
  public licenses: IHospitalLicense[] = [];
  public license: IHospitalLicense = {
    id: 0,
    userId: "",
    licenseId: "",
    enteredByStaff: false,
    assignedToId: "",
    assignedToName: "",
    finalApprovalBy: "",
    canRenew: false,
    isRenewal: false,
    electronicSignature: "",
    signedDate: "",
    adminPrintTitle: "",
    applicationDate: "",
    approvedDate: "",
    deniedDate: "",
    expirationDate: "",
    entityName: "",
    hospitalDBAName: "",
    hospitalPhone: "",
    hospitalEmail: "",
    hospitalFax: "",
    classification: "",
    isSicf: false,
    mainHospitalOutOfState: null,
    hasUnionRepresentation: null,
    existingLicense: "",
    accredited: false,
    accreditingAgency: "",
    deemed: false,
    fiscalYearEnd: "",
    ownershipCategory: "",
    bypassDuplicateManagerValidation: false,
    hospitalAdminName: "",
    hospitalAdminPhone: "",
    hospitalAdminEmail: "",
    emergencyContactName: "",
    emergencyContactPhone: "",
    emergencyContactEmail: "",
    chiefNurseName: "",
    chiefNursePhone: "",
    chiefNurseEmail: "",
    filerName: "",
    filerPhone: "",
    filerEmail: "",
    nurseManagerName: "",
    nurseManagerTitle: "",
    nurseManagerEmail: "",
    nurseManagerPhone: "",
    directCareName: "",
    directCareTitle: "",
    directCareEmail: "",
    directCarePhone: "",
    emergencyDepartmentDirectorName: '',
    emergencyDepartmentDirectorPhone: '',
    emergencyDepartmentDirectorEmail: '',
    technicalManagerCoChairName: '',
    technicalManagerCoChairPhone: '',
    technicalManagerCoChairEmail: '',
    technicalStaffCoChairName: '',
    technicalStaffCoChairPhone: '',
    technicalStaffCoChairEmail: '',
    serviceStaffingCommitteeManagerCoChairName: '',
    serviceStaffingCommitteeManagerCoChairPhone: '',
    serviceStaffingCommitteeManagerCoChairEmail: '',
    serviceStaffingCommitteeStaffCoChairName: '',
    serviceStaffingCommitteeStaffCoChairPhone: '',
    serviceStaffingCommitteeStaffCoChairEmail: '',
    inpatientBeds: 0,
    inpatientPsychiatricBeds: 0,
    satelliteInpatientPsychiatricBeds: 0,
    swingBeds: false,
    swingBedsCount: 0,
    maternity: false,
    surgical: false,
    emergency: false,
    backgroundCheckDate: "",
    archived: false,
    statuses: [],
    representativeContacts: []
  }
  public staff: IStaff[] = [];

  public chiefNurseValidator = inject(HospitalChiefNurseValidator);

    public readonly contactNameErrorMatchers = Object.fromEntries(
    Object.keys(hospitalContactNameLabels).map(field => [field, new HospitalContactNameErrorStateMatcher(field)])
  );

  public licenseForm = new FormGroup({
    licenseId: new FormControl('', Validators.required),
    classification: new FormControl('', Validators.required),
    isSicf: new FormControl<boolean | null>(null, Validators.required),
    hospitalDBAName: new FormControl(''),
    hospitalPhone: new FormControl('', [Validators.required, Validators.pattern(/^\(?([0-9]{3})\)?[-]?([0-9]{3})[-]?([0-9]{4}).*$/)]),
    hospitalEmail: new FormControl('', [Validators.required, Validators.email]),
    hospitalFax: new FormControl('', [Validators.pattern(/^\(?([0-9]{3})\)?[-]?([0-9]{3})[-]?([0-9]{4}).*$/)]),
    existingLicense: new FormControl(''),
    accredited: new FormControl<boolean | null>(null, Validators.required),
    accreditingAgency: new FormControl('', Validators.required),
    deemed: new FormControl<boolean | null>(null, Validators.required),
    fiscalYearEnd: new FormControl('', [Validators.required, Validators.pattern(/^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])$/)]),
    ownershipCategory: new FormControl('', Validators.required),
    entityName: new FormControl('', Validators.required),
    electronicSignature: new FormControl('', Validators.required),
    signedDate: new FormControl<Date | null>(null, Validators.required),
    approvedDate: new FormControl<Date | null>(null),
    expirationDate: new FormControl<Date | null>(null),
    adminPrintTitle: new FormControl('', Validators.required),
    mainHospitalOutOfState: new FormControl<boolean | null>(null, Validators.required),
    hasUnionRepresentation: new FormControl<boolean | null>(null, Validators.required),
    bypassDuplicateManagerValidation: new FormControl<boolean>(false),
    emergencyContactName: new FormControl('', Validators.required),
    emergencyContactPhone: new FormControl('', [Validators.required, Validators.pattern(/^\(?([0-9]{3})\)?[-]?([0-9]{3})[-]?([0-9]{4}).*$/)]),
    emergencyContactEmail: new FormControl('', [Validators.required, Validators.email]),
    hospitalAdminName: new FormControl('', Validators.required),
    hospitalAdminPhone: new FormControl('', [Validators.required, Validators.pattern(/^\(?([0-9]{3})\)?[-]?([0-9]{3})[-]?([0-9]{4}).*$/)]),
    hospitalAdminEmail: new FormControl('', [Validators.required, Validators.email]),
    chiefNurseName: new FormControl('', {
      validators: Validators.required,
      asyncValidators: [this.chiefNurseValidator.validate.bind(this.chiefNurseValidator)],
      updateOn: 'blur'
    }),
    chiefNursePhone: new FormControl('', [Validators.required, Validators.pattern(/^\(?([0-9]{3})\)?[-]?([0-9]{3})[-]?([0-9]{4}).*$/)]),
    chiefNurseEmail: new FormControl('', [Validators.required, Validators.email]),
    filerName: new FormControl('', Validators.required),
    filerPhone: new FormControl('', [Validators.required, Validators.pattern(/^\(?([0-9]{3})\)?[-]?([0-9]{3})[-]?([0-9]{4}).*$/)]),
    filerEmail: new FormControl('', [Validators.required, Validators.email]),
    nurseManagerName: new FormControl('', Validators.required),
    nurseManagerTitle: new FormControl('', Validators.required),
    nurseManagerEmail: new FormControl('', [Validators.required, Validators.email]),
    nurseManagerPhone: new FormControl('', [Validators.required, Validators.pattern(/^\(?([0-9]{3})\)?[-]?([0-9]{3})[-]?([0-9]{4}).*$/)]),
    directCareName: new FormControl('', [Validators.required]),
    directCareTitle: new FormControl('', Validators.required),
    directCareEmail: new FormControl('', [Validators.required, Validators.email]),
    directCarePhone: new FormControl('', [Validators.required, Validators.pattern(/^\(?([0-9]{3})\)?[-]?([0-9]{3})[-]?([0-9]{4}).*$/)]),
    emergencyDepartmentDirectorName: new FormControl('', Validators.required),
    emergencyDepartmentDirectorPhone: new FormControl('', [Validators.required, Validators.pattern(/^\(?([0-9]{3})\)?[-]?([0-9]{3})[-]?([0-9]{4}).*$/)]),
    emergencyDepartmentDirectorEmail: new FormControl('', [Validators.required, Validators.email]),
    technicalManagerCoChairName: new FormControl('', Validators.required),
    technicalManagerCoChairPhone: new FormControl('', [Validators.required, Validators.pattern(/^\(?([0-9]{3})\)?[-]?([0-9]{3})[-]?([0-9]{4}).*$/)]),
    technicalManagerCoChairEmail: new FormControl('', [Validators.required, Validators.email]),
    technicalStaffCoChairName: new FormControl('', Validators.required),
    technicalStaffCoChairPhone: new FormControl('', [Validators.required, Validators.pattern(/^\(?([0-9]{3})\)?[-]?([0-9]{3})[-]?([0-9]{4}).*$/)]),
    technicalStaffCoChairEmail: new FormControl('', [Validators.required, Validators.email]),
    serviceStaffingCommitteeManagerCoChairName: new FormControl('', Validators.required),
    serviceStaffingCommitteeManagerCoChairPhone: new FormControl('', [Validators.required, Validators.pattern(/^\(?([0-9]{3})\)?[-]?([0-9]{3})[-]?([0-9]{4}).*$/)]),
    serviceStaffingCommitteeManagerCoChairEmail: new FormControl('', [Validators.required, Validators.email]),
    serviceStaffingCommitteeStaffCoChairName: new FormControl('', [Validators.required]),
    serviceStaffingCommitteeStaffCoChairPhone: new FormControl('', [Validators.required, Validators.pattern(/^\(?([0-9]{3})\)?[-]?([0-9]{3})[-]?([0-9]{4}).*$/)]),
    serviceStaffingCommitteeStaffCoChairEmail: new FormControl('', [Validators.required, Validators.email]),
    inpatientBeds: new FormControl<number | null>(null, [Validators.required, Validators.min(0)]),
    inpatientPsychiatricBeds: new FormControl<number | null>(null, [Validators.required, Validators.min(0)]),
    satelliteInpatientPsychiatricBeds: new FormControl<number | null> (null, [Validators.required, Validators.min(0)]),
    swingBeds: new FormControl<boolean | null>(null, Validators.required),
    swingBedsCount: new FormControl<number | null>(null),
    maternity: new FormControl<boolean | null>(false),
    surgical: new FormControl<boolean | null>(false),
    emergency: new FormControl<boolean | null>(false),
    status: new FormControl(0),
  }, {
    validators: hospitalContactNamesValidator(() => this.license.bypassDuplicateManagerValidation === true)
  });

  public representativeContactForm = new FormGroup({
    representativeType: new FormControl('', Validators.required),
    representativeTypeOther: new FormControl(''),
    representativeStaff: new FormControl('', Validators.required),
    contactName: new FormControl('', Validators.required),
    contactPhone: new FormControl('', [Validators.required, Validators.pattern(/^\(?([0-9]{3})\)?[-]?([0-9]{3})[-]?([0-9]{4}).*$/)]),
    contactEmail: new FormControl('', [Validators.required, Validators.email])
  }, {
    validators: [this.representativeTypeOtherValidator()]
  });

  public searchForm = new FormGroup({
    searchTerm: new FormControl(''),
    entityGuid: new FormControl(false),
    entityName: new FormControl(false),
    assignedTo: new FormControl(''),
    includeArchived: new FormControl(false),
    statuses: new FormControl<any[]>([])
  });

  public editingRepresentativeContact: IRepresentativeContact | null = null;
  public showRepresentativeContactForm: boolean = false;

  public representativeContactColumns: string [] = ['representativeType', 'representativeStaff', 'contactName', 'contactPhone', 'contactEmail', 'actions'];
  public representativeContactDataSource = new MatTableDataSource<IRepresentativeContact>(this.license.representativeContacts);
  @ViewChild('representativeContactPaginator', {static: false}) representativeContactPaginator: MatPaginator | undefined;
  @ViewChild('representativeContactSort', {static: false}) representativeContactSort: MatSort | undefined;

  public representativeTypeOptions: string[] = [
    'Oregon Nurses Association (ONA)',
    'Oregon Federation of Nurses and Healthcare Professionals (OFNHP)',
    'Service Employees International Union (SEIU)',
    'American Federation of State, County and Municipal Employees (AFSCME)',
    'Other'
  ];

  private readonly outOfStateOptionalControls = [
    'nurseManagerName',
    'nurseManagerTitle',
    'nurseManagerEmail',
    'directCareName',
    'directCareTitle',
    'directCareEmail',
    'emergencyDepartmentDirectorName',
    'emergencyDepartmentDirectorPhone',
    'emergencyDepartmentDirectorEmail',
    'technicalManagerCoChairName',
    'technicalManagerCoChairPhone',
    'technicalManagerCoChairEmail',
    'technicalStaffCoChairName',
    'technicalStaffCoChairPhone',
    'technicalStaffCoChairEmail',
    'serviceStaffingCommitteeManagerCoChairName',
    'serviceStaffingCommitteeManagerCoChairPhone',
    'serviceStaffingCommitteeManagerCoChairEmail',
    'serviceStaffingCommitteeStaffCoChairName',
    'serviceStaffingCommitteeStaffCoChairPhone',
    'serviceStaffingCommitteeStaffCoChairEmail'
  ];

  public licenseColumns: string [] = ['licenseId', 'isSicf', 'hospitalDBAName', 'statuses', 'hospitalPhone', 'hospitalEmail', 'assignedToName'];
  public licenseSource = new MatTableDataSource<IHospitalLicense>(this.licenses);
  @ViewChild('licensePaginator', {static: false}) licensePaginator!: MatPaginator;
  @ViewChild('licenseSort', {static: false}) licenseSort!: MatSort;

  public sharedService = inject(SharedService);
  public licenseService = inject(HospitalLicenseService);
  public authService = inject(AuthService);
  public dialog = inject(MatDialog);
  public router = inject(Router);
  public route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    this.id = Number(this.route.snapshot.paramMap.get('id'));
    this.renewal = this.router.url.includes('renewal');
    this.assigned = this.router.url.includes('assigned');
  }

  ngOnInit(): void {
    if(this.renewal){
      this.searchForm.get('statuses')?.setValue(['Renewal']);
    }
    this.assigned ? this.getAssignedLicenses() : this.searchLicenses();
    this.getStaff();
    this.sharedService.getUserTheme();
    this.sharedService.getStatuses();
    this.configureOutOfStateValidation();
  }

  getAssignedLicenses(): void {
    this.licenseService.getAssignedLicenses().subscribe({
      next: response => {
        this.licenses = response;
      },
      error: error => {
        console.log('error getting assigned licenses', error);
        this.sharedService.toastr.error('An error occurred while getting assigned licenses.');
      },
      complete: () => {
        this.updateLicenseTable();
      }
    });
  }

  searchLicenses(): void {
    const form = this.searchForm.getRawValue();
    this.search.searchTerm = form.searchTerm;
    this.search.entityGuid = form.entityGuid;
    this.search.entityName = form.entityName;
    this.search.assignedTo = form.assignedTo;
    this.search.includeArchived = form.includeArchived;
    this.search.statuses = form.statuses || [];

    this.licenseService.searchLicenses(this.search).subscribe({
      next: response => {
        this.licenses = response;
      },
      error: error => {
        console.log('error searching for licenses', error);
        this.sharedService.toastr.error('An error occurred while searching for licenses.');
      },
      complete: () => {
        this.updateLicenseTable();
      }
    });
  }

  getStaff(): void {
    this.licenseService.getStaff(this.hospital).subscribe({
      next: response => {
        this.staff = response;
      },
      error: error => {
        console.log('error getting staff', error);
        this.sharedService.toastr.error('An error occurred while getting staff.');
      }
    });
  }

  private configureOutOfStateValidation(): void {
    const outOfStateControl = this.licenseForm.controls.mainHospitalOutOfState;
    outOfStateControl.valueChanges
      .pipe(
        startWith(outOfStateControl.value),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(isOutOfState => {
        this.outOfStateOptionalControls.forEach(controlName => {
          const control = this.licenseForm.get(controlName);

          if (isOutOfState === true) {
            control?.removeValidators(Validators.required);
          } else {
            control?.addValidators(Validators.required);
          }

          control?.updateValueAndValidity({ emitEvent: false });
        });
      });
  }

  updateLicenseTable(): void {
    setTimeout(() => {
      this.licenseSource.data = this.licenses;
      this.licenseSource.paginator = this.licensePaginator;
      this.licenseSource.sort = this.licenseSort;
    }, 500);
  }

  selectLicense(license: IHospitalLicense): void {
    this.license = license;
    this.chiefNurseValidator.licenseId = license.licenseId ?? '';
    this.licenseForm.get('chiefNurseName')?.updateValueAndValidity();
    this.updateLicenseForm(license);
    this.updateRepresentativeContactsTable();
  }

  toggleEditing(editing: boolean): void {
    this.editing = editing;
  }

  updateLicense(): void {
    const form = this.licenseForm.getRawValue();
    this.license.licenseId = form.licenseId ?? '';
    this.license.classification = form.classification ?? '';
    this.license.isSicf = form.isSicf ?? false;
    this.license.entityName = form.entityName ?? '';
    this.license.hospitalDBAName = form.hospitalDBAName ?? '';
    this.license.hospitalPhone = form.hospitalPhone ?? '';
    this.license.hospitalEmail = form.hospitalEmail ?? '';
    this.license.hospitalFax = form.hospitalFax ?? '';
    this.license.existingLicense = form.existingLicense ?? '';
    this.license.accredited = form.accredited ?? false;
    this.license.accreditingAgency = form.accreditingAgency ?? '';
    this.license.deemed = form.deemed ?? false;
    this.license.fiscalYearEnd = form.fiscalYearEnd ?? '';
    this.license.electronicSignature = form.electronicSignature ?? '';
    this.license.adminPrintTitle = form.adminPrintTitle ?? '';
    this.license.signedDate = form.signedDate ? form.signedDate.toISOString() : '';
    this.license.approvedDate = form.approvedDate ? form.approvedDate.toISOString() : '';
    this.license.expirationDate = form.expirationDate ? form.expirationDate.toISOString() : '';
    if (Array.isArray(form.ownershipCategory)) {
      this.license.ownershipCategory = JSON.stringify(form.ownershipCategory);
    } else {
      this.license.ownershipCategory = form.ownershipCategory ?? '';
    }
    this.license.hasUnionRepresentation = form.hasUnionRepresentation ?? false;
    this.license.mainHospitalOutOfState = form.mainHospitalOutOfState ?? false;
    this.license.bypassDuplicateManagerValidation = form.bypassDuplicateManagerValidation ?? false;
    this.license.hospitalAdminName = form.hospitalAdminName ?? '';
    this.license.hospitalAdminPhone = form.hospitalAdminPhone ?? '';
    this.license.hospitalAdminEmail = form.hospitalAdminEmail ?? '';
    this.license.emergencyContactName = form.emergencyContactName ?? '';
    this.license.emergencyContactPhone = form.emergencyContactPhone ?? '';
    this.license.emergencyContactEmail = form.emergencyContactEmail ?? '';
    this.license.chiefNurseName = form.chiefNurseName ?? '';
    this.license.chiefNursePhone = form.chiefNursePhone ?? '';
    this.license.chiefNurseEmail = form.chiefNurseEmail ?? '';
    this.license.filerName = form.filerName ?? '';
    this.license.filerPhone = form.filerPhone ?? '';
    this.license.filerEmail = form.filerEmail ?? '';
    this.license.nurseManagerName = form.nurseManagerName ?? '';
    this.license.nurseManagerTitle = form.nurseManagerTitle ?? '';
    this.license.nurseManagerEmail = form.nurseManagerEmail ?? '';
    this.license.nurseManagerPhone = form.nurseManagerPhone ?? '';
    this.license.directCareName = form.directCareName ?? '';
    this.license.directCareTitle = form.directCareTitle ?? '';
    this.license.directCareEmail = form.directCareEmail ?? '';
    this.license.directCarePhone = form.directCarePhone ?? '';
    this.license.emergencyDepartmentDirectorName = form.emergencyDepartmentDirectorName ?? '';
    this.license.emergencyDepartmentDirectorPhone = form.emergencyDepartmentDirectorPhone ?? '';
    this.license.emergencyDepartmentDirectorEmail = form.emergencyDepartmentDirectorEmail ?? '';
    this.license.technicalManagerCoChairName = form.technicalManagerCoChairName;
    this.license.technicalManagerCoChairPhone = form.technicalManagerCoChairPhone;
    this.license.technicalManagerCoChairEmail = form.technicalManagerCoChairEmail;
    this.license.technicalStaffCoChairName = form.technicalStaffCoChairName;
    this.license.technicalStaffCoChairPhone = form.technicalStaffCoChairPhone;
    this.license.technicalStaffCoChairEmail = form.technicalStaffCoChairEmail;
    this.license.serviceStaffingCommitteeManagerCoChairName = form.serviceStaffingCommitteeManagerCoChairName;
    this.license.serviceStaffingCommitteeManagerCoChairPhone = form.serviceStaffingCommitteeManagerCoChairPhone;
    this.license.serviceStaffingCommitteeManagerCoChairEmail = form.serviceStaffingCommitteeManagerCoChairEmail;
    this.license.serviceStaffingCommitteeStaffCoChairName = form.serviceStaffingCommitteeStaffCoChairName;
    this.license.serviceStaffingCommitteeStaffCoChairPhone = form.serviceStaffingCommitteeStaffCoChairPhone;
    this.license.serviceStaffingCommitteeStaffCoChairEmail = form.serviceStaffingCommitteeStaffCoChairEmail;
    this.license.inpatientBeds = form.inpatientBeds ?? 0;
    this.license.inpatientPsychiatricBeds = form.inpatientPsychiatricBeds ?? 0;
    this.license.satelliteInpatientPsychiatricBeds = form.satelliteInpatientPsychiatricBeds ?? 0;
    this.license.swingBeds = form.swingBeds ?? false;
    this.license.swingBedsCount = form.swingBedsCount ?? 0;
    this.license.maternity = form.maternity ?? false;
    this.license.surgical = form.surgical ?? false;
    this.license.emergency = form.emergency ?? false;
  }

  updateLicenseForm(license: IHospitalLicense): void {
    this.licenseForm.reset({
      licenseId: license.licenseId,
      classification: license.classification,
      isSicf: license.isSicf,
      hospitalDBAName: license.hospitalDBAName,
      hospitalPhone: license.hospitalPhone,
      hospitalEmail: license.hospitalEmail,
      hospitalFax: license.hospitalFax,
      existingLicense: license.existingLicense,
      accredited: license.accredited,
      accreditingAgency: license.accreditingAgency,
      deemed: license.deemed,
      fiscalYearEnd: license.fiscalYearEnd,
      ownershipCategory: license.ownershipCategory ? JSON.parse(license.ownershipCategory) : null,
      entityName: license.entityName,
      electronicSignature: license.electronicSignature,
      adminPrintTitle: license.adminPrintTitle,
      hasUnionRepresentation: license.hasUnionRepresentation,
      mainHospitalOutOfState: license.mainHospitalOutOfState,
      bypassDuplicateManagerValidation: license.bypassDuplicateManagerValidation,
      emergencyContactName: license.emergencyContactName,
      emergencyContactPhone: license.emergencyContactPhone,
      emergencyContactEmail: license.emergencyContactEmail,
      hospitalAdminName: license.hospitalAdminName,
      hospitalAdminPhone: license.hospitalAdminPhone,
      hospitalAdminEmail: license.hospitalAdminEmail,
      chiefNurseName: license.chiefNurseName,
      chiefNursePhone: license.chiefNursePhone,
      chiefNurseEmail: license.chiefNurseEmail,
      filerName: license.filerName,
      filerPhone: license.filerPhone,
      filerEmail: license.filerEmail,
      nurseManagerName: license.nurseManagerName,
      nurseManagerTitle: license.nurseManagerTitle,
      nurseManagerEmail: license.nurseManagerEmail,
      nurseManagerPhone: license.nurseManagerPhone,
      directCareName: license.directCareName,
      directCareTitle: license.directCareTitle,
      directCareEmail: license.directCareEmail,
      directCarePhone: license.directCarePhone,
      emergencyDepartmentDirectorName: license.emergencyDepartmentDirectorName,
      emergencyDepartmentDirectorPhone: license.emergencyDepartmentDirectorPhone,
      emergencyDepartmentDirectorEmail: license.emergencyDepartmentDirectorEmail,
      technicalManagerCoChairName: license.technicalManagerCoChairName,
      technicalManagerCoChairPhone: license.technicalManagerCoChairPhone,
      technicalManagerCoChairEmail: license.technicalManagerCoChairEmail,
      technicalStaffCoChairName: license.technicalStaffCoChairName,
      technicalStaffCoChairPhone: license.technicalStaffCoChairPhone,
      technicalStaffCoChairEmail: license.technicalStaffCoChairEmail,
      serviceStaffingCommitteeManagerCoChairName: license.serviceStaffingCommitteeManagerCoChairName,
      serviceStaffingCommitteeManagerCoChairPhone: license.serviceStaffingCommitteeManagerCoChairPhone,
      serviceStaffingCommitteeManagerCoChairEmail: license.serviceStaffingCommitteeManagerCoChairEmail,
      serviceStaffingCommitteeStaffCoChairName: license.serviceStaffingCommitteeStaffCoChairName,
      serviceStaffingCommitteeStaffCoChairPhone: license.serviceStaffingCommitteeStaffCoChairPhone,
      serviceStaffingCommitteeStaffCoChairEmail: license.serviceStaffingCommitteeStaffCoChairEmail,
      inpatientBeds: license.inpatientBeds,
      inpatientPsychiatricBeds: license.inpatientPsychiatricBeds,
      satelliteInpatientPsychiatricBeds: license.satelliteInpatientPsychiatricBeds,
      swingBeds: license.swingBeds,
      swingBedsCount: license.swingBedsCount,
      maternity: license.maternity,
      surgical: license.surgical,
      emergency: license.emergency,
    });

    if(license.signedDate){
      this.licenseForm.get('signedDate')?.setValue(new Date(license.signedDate));
    }
    if(license.approvedDate){
      this.licenseForm.get('approvedDate')?.setValue(new Date(license.approvedDate));
    }
    if(license.expirationDate){
      this.licenseForm.get('expirationDate')?.setValue(new Date(license.expirationDate));
    }
  }

  getEmptyLicense(): IHospitalLicense {
    return {
      id: 0,
      userId: "",
      licenseId: "",
      enteredByStaff: false,
      assignedToId: "",
      assignedToName: "",
      finalApprovalBy: "",
      canRenew: false,
      isRenewal: false,
      electronicSignature: "",
      signedDate: "",
      adminPrintTitle: "",
      applicationDate: "",
      approvedDate: "",
      deniedDate: "",
      expirationDate: "",
      entityName: "",
      hospitalDBAName: "",
      hospitalPhone: "",
      hospitalEmail: "",
      hospitalFax: "",
      classification: "",
      mainHospitalOutOfState: null,
      hasUnionRepresentation: null,
      isSicf: false,
      existingLicense: "",
      accredited: false,
      accreditingAgency: "",
      deemed: false,
      fiscalYearEnd: "",
      ownershipCategory: "",
      bypassDuplicateManagerValidation: false,
      hospitalAdminName: "",
      hospitalAdminPhone: "",
      hospitalAdminEmail: "",
      emergencyContactName: "",
      emergencyContactPhone: "",
      emergencyContactEmail: "",
      chiefNurseName: "",
      chiefNursePhone: "",
      chiefNurseEmail: "",
      filerName: "",
      filerPhone: "",
      filerEmail: "",
      nurseManagerName: "",
      nurseManagerTitle: "",
      nurseManagerEmail: "",
      nurseManagerPhone: "",
      directCareName: "",
      directCareTitle: "",
      directCareEmail: "",
      directCarePhone: "",
      emergencyDepartmentDirectorName: '',
      emergencyDepartmentDirectorPhone: '',
      emergencyDepartmentDirectorEmail: '',
      technicalManagerCoChairName: '',
      technicalManagerCoChairPhone: '',
      technicalManagerCoChairEmail: '',
      technicalStaffCoChairName: '',
      technicalStaffCoChairPhone: '',
      technicalStaffCoChairEmail: '',
      serviceStaffingCommitteeManagerCoChairName: '',
      serviceStaffingCommitteeManagerCoChairPhone: '',
      serviceStaffingCommitteeManagerCoChairEmail: '',
      serviceStaffingCommitteeStaffCoChairName: '',
      serviceStaffingCommitteeStaffCoChairPhone: '',
      serviceStaffingCommitteeStaffCoChairEmail: '',
      inpatientBeds: 0,
      inpatientPsychiatricBeds: 0,
      satelliteInpatientPsychiatricBeds: 0,
      swingBeds: false,
      swingBedsCount: 0,
      maternity: false,
      surgical: false,
      emergency: false,
      backgroundCheckDate: "",
      archived: false,
      statuses: [],
      representativeContacts: []
    }
  }

  closeLicense(): void {
    this.license = this.getEmptyLicense();
    this.editing = false;
    this.paymentsLoaded = false;
    this.updateLicenseForm(this.license);
    this.updateLicenseTable();
  }

  createLicense(): void {
    this.sharedService.openConfirm('Create a new license?');
    this.sharedService.confirmed().subscribe(
      confirmed => {
        if(confirmed){
          this.licenseService.createLicense().subscribe({
            next: response => {
              this.editing = true;
              this.license = response;
              this.updateLicenseForm(response);
            },
            error: error => {
              console.log('error creating license', error);
              this.sharedService.toastr.error('An error occurred while creating the license.');
            },
            complete: () => {
              this.licenses.push(this.license);
              this.sharedService.toastr.success('License created successfully.');
            }
          });
        }
      }
    );
  }

  saveLicense(): void {
    this.sharedService.openConfirm('Update this license?');
    this.sharedService.confirmed().subscribe(
      confirmed => {
        if(confirmed){
          this.updateLicense();
          this.licenseService.updateLicense(this.license).subscribe({
            complete: () => {
              const newStatus = this.licenseForm.get('status')?.value;
              if (newStatus !== null && newStatus !== undefined && !this.license.statuses.some(s => s.id === newStatus)) {
                this.updateLicenseStatus(newStatus);
              }
              else {
                this.sharedService.toastr.success('License updated successfully.');
              }
            },
            error: error => {
              console.log('error updating license', error);
              this.sharedService.toastr.error('An error occurred while updating the license.');
            }
          });
        }
      }
    );
  }

  updateLicenseStatus(statusId: number): void {
    this.licenseService.updateLicenseStatus(this.license.id, statusId).subscribe({
      next: response => {
        this.license = response;
        this.updateLicenseForm(this.license);
      },
      error: error => {
        console.log('error updating license status', error);
        this.sharedService.toastr.error('An error occurred while updating the license status.');
      },
      complete: () => {
        this.sharedService.toastr.success('License updated successfully.');
        let index: number = this.licenses.findIndex(l => l.id === this.license.id);
        this.licenses[index] = this.license;
      }
    });
  }

  archiveLicense(license: IHospitalLicense, archive: boolean): void {
    let archiving: string = archive? 'Archive ' : 'Un-Archive ';
    this.sharedService.openConfirm(archiving + 'this license?');
    this.sharedService.confirmed().subscribe(
      confirmed => {
        if(confirmed){
          this.licenseService.archiveLicense(license.id, archive).subscribe({
            complete: () => {
              license.archived = archive;
              if(archive){
                this.licenses = this.licenses.filter(o => o.id !== license.id);
                this.closeLicense();
              }
            },
            error: e => {
              this.sharedService.toastr.error("Could Not Archive License");
              console.log('error archiving license', e);
            }
          });
        }
      }
    );
  }

  deleteLicense(licenseId: number): void {
    this.sharedService.openConfirm('Delete this license?');
    this.sharedService.confirmed().subscribe(
      confirmed => {
        if(confirmed){
          this.licenseService.deleteHospitalLicense(licenseId).subscribe({
            complete: () => {
              this.licenses = this.licenses.filter(o => o.id !== licenseId);
              this.closeLicense();
            },
            error: e => {
              this.sharedService.toastr.error("Could Not Delete License");
              console.log('error deleting license', e);
            }
          });
        }
      }
    );
  }

  assignLicense(event: MatSelectChange, licenseId: number): void {
    let user: IStaff | undefined = this.staff.find(staff => staff.name === event.value);
    if(user === undefined){
      let emptyUser: IStaff = {
        id: '',
        name: '',
        email: ''
      }
      user = emptyUser;
    }
    this.licenseService.assignLicense(licenseId, user).subscribe({
      complete: () => {
        const license = this.licenses.find(tp => tp.id == licenseId);
        if (license) {
          license.assignedToName = user.name;
        }
        if(user === null || user.name === null || user.name.length === 0) {
          this.sharedService.toastr.success("License unassigned");
        }
        else {
          this.sharedService.toastr.success("License assigned to " + user.name);
        }
      },
      error: error => {
        console.log('error assigning license', error);
        this.sharedService.toastr.error('An error occurred while assigning the license.');
      }
    });
  }

  viewAuditLog(): void {
    this.sharedService.openAuditLog(this.license.id, this.sharedService.hospitalLicense);
  }

  paymentsLoadedEvent(loaded: boolean): void {
    this.paymentsLoaded = loaded;
  }

  openContactsPdfDialog(): void {
    this.dialog.open(HospitalContactsExportComponent, {
      data: this.license,
      role: 'dialog',
      ariaLabel: "Hospital Contacts PDF Dialog",
      minWidth: '950px',
      maxWidth: '950px'
    });
  }

  // Representative Contacts
  representativeTypeOtherValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const representativenType = control.value.representativenType;
      const representativenTypeOther = control.value.representativenTypeOther;

      if (representativenType === 'Other' && (!representativenTypeOther || representativenTypeOther.trim() === '')) {
        return { representativeTypeOtherRequired: true };
      }
      return null;
    }
  }

  updateRepresentativeContactsTable(): void {
    this.representativeContactDataSource.data = [...this.license.representativeContacts];
    setTimeout(() => {
      if (this.representativeContactPaginator) {
        this.representativeContactDataSource.paginator = this.representativeContactPaginator;
      }
      if (this.representativeContactSort) {
        this.representativeContactDataSource.sort = this.representativeContactSort;
      }
    }, 100);
  }

  showAddRepresentativeContactForm(): void {
    this.showRepresentativeContactForm = true;
    this.editingRepresentativeContact = null;
    this.representativeContactForm.reset();
  }

  saveRepresentativeContact(): void {
    if (this.representativeContactForm.invalid) {
      return;
    }

    const formValue = this.representativeContactForm.value;
    const representativeType = formValue.representativeType === 'Other'
      ? formValue.representativeTypeOther
      : formValue.representativeType;

    if (this.editingRepresentativeContact) {
      const updatedContact: IRepresentativeContact = {
        ...this.editingRepresentativeContact,
        representativeType: representativeType || null,
        representativeStaff: formValue.representativeStaff || null,
        contactName: formValue.contactName || null,
        contactPhone: formValue.contactPhone || null,
        contactEmail: formValue.contactEmail || null
      };

      this.licenseService.updateRepresentativeContact(updatedContact).subscribe({
        next: () => {
          const index = this.license.representativeContacts.findIndex(c => c.id === this.editingRepresentativeContact!.id);
          if (index !== -1) {
            this.license.representativeContacts[index] = updatedContact;
          }
          this.updateRepresentativeContactsTable();
          this.sharedService.toastr.success('Representative contact updated successfully');
          this.editingRepresentativeContact = null;
          this.representativeContactForm.reset();
          this.showRepresentativeContactForm = false;
        },
        error: (e) => {
          console.log('error updating representative contact', e);
          this.sharedService.toastr.error('Error updating representative contact');
        }
      });
    } else {
      const newContact: IRepresentativeContact = {
        id: 0,
        userId: this.authService.email,
        representativeType: representativeType || null,
        representativeStaff: formValue.representativeStaff || null,
        contactName: formValue.contactName || null,
        contactPhone: formValue.contactPhone || null,
        contactEmail: formValue.contactEmail || null,
        hospitalLicenseId: this.license.id
      };

      this.licenseService.addRepresentativeContact(newContact).subscribe({
        next: (result) => {
          newContact.id = result.id;
          this.license.representativeContacts.push(newContact);
          this.updateRepresentativeContactsTable();
          this.sharedService.toastr.success('Representative contact added successfully');
          this.representativeContactForm.reset();
          this.showRepresentativeContactForm = false;
        },
        error: (e) => {
          console.log('error adding representative contact', e);
          this.sharedService.toastr.error('Error adding representative contact');
        }
      });
    }
  }

  editRepresentativeContact(contact: IRepresentativeContact): void {
    this.editingRepresentativeContact = contact;
    this.showRepresentativeContactForm = true;

    const isPredefinedOption = this.representativeTypeOptions.includes(contact.representativeType || '');

    this.representativeContactForm.patchValue({
      representativeType: isPredefinedOption ? contact.representativeType : 'Other',
      representativeTypeOther: isPredefinedOption ? '' : contact.representativeType,
      representativeStaff: contact.representativeStaff,
      contactName: contact.contactName,
      contactPhone: contact.contactPhone,
      contactEmail: contact.contactEmail
    });
  }

  deleteRepresentativeContact(contact: IRepresentativeContact): void {
    this.sharedService.openConfirm('Are you sure you want to delete this representative contact?');
    this.sharedService.confirmed().subscribe(confirmed => {
      if (confirmed) {
        this.licenseService.deleteRepresentativeContact(contact.id).subscribe({
          next: () => {
            this.license.representativeContacts = this.license.representativeContacts.filter(c => c.id !== contact.id);
            this.updateRepresentativeContactsTable();
            this.sharedService.toastr.success('Representative contact deleted successfully');
          },
          error: (e) => {
            console.log('error deleting representative contact', e);
            this.sharedService.toastr.error('Error deleting representative contact');
          }
        });
      }
    });
  }

  cancelEditRepresentativeContact(): void {
    this.editingRepresentativeContact = null;
    this.showRepresentativeContactForm = false;
    this.representativeContactForm.reset();
    this.updateRepresentativeContactsTable();
  }
}
